import {useEffect, useRef} from 'preact/hooks';
import {QuestionMarkCircleIcon} from "@heroicons/react/24/outline/index.js";
import {ArrowRightIcon, PlayIcon, ArrowTopRightOnSquareIcon} from "@heroicons/react/24/solid";

export function Playlist({playlist}) {
    const {
        id,
        category_name,
        description,
        youtube_url,
        description_url,
        special,
        image_url,
        full_view = true
    } = playlist;

    const imgRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    element.style.backgroundImage = `url(${element.dataset.src})`;
                    observer.unobserve(element);
                }
            });
        });

        if (imgRef.current) {
            imgRef.current.style.backgroundImage = "";
            observer.observe(imgRef.current);
        }

        return () => {
            if (imgRef.current) {
                observer.unobserve(imgRef.current);
            }
        };
    }, [youtube_url]);

    return (
        <div
            className="relative">

            {id === 'new' ? (
                <span
                    className="z-1 pointer-events-none absolute left-3 top-3 text-gray-300 text-xs bg-red-400 px-2 py-0.5 rounded-lg">New</span>
            ) : id === 'network' ? (
                <span
                    className="z-1 pointer-events-none absolute left-3 top-3 text-gray-300 text-xs bg-black/40 px-2 py-0.5 rounded-lg">Network</span>
            ) : (
                <>
                    {description && (
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={description_url}
                            aria-label={`Playlist description ${category_name}`}
                            className="absolute right-10 top-3 p-1 bg-black/20 rounded-sm cursor-pointer group z-10"
                        >
                            <span
                                className="absolute bottom-full right-0 mb-2 w-[200px] p-2 text-sm text-black bg-gray-100 rounded-sm shadow-lg invisible group-hover:visible group-hover:opacity-100 opacity-0 transition-all duration-150 translate-y-1 group-hover:translate-y-0 pointer-events-none"
                                role="tooltip"
                            >
                                {description}
                            </span>
                            <QuestionMarkCircleIcon className="text-white" height={18}/>
                        </a>
                    )}
                    {full_view && (
                        <a href={`/playlist?p=${id}`}
                           aria-label={`Playlist ${category_name}`}
                           className="z-1 absolute right-3 top-3 rounded-sm bg-black/20 p-1 cursor-pointer">
                            <ArrowTopRightOnSquareIcon className={'text-white'} height={18}/>
                        </a>
                    )}
                </>
            )}

            {special ? (
                <span style={{left: 0, borderRadius: '0 0.25rem 0.25rem 0'}}
                      className="z-1 pointer-events-none absolute left-3 top-3 text-gray-300 text-xs bg-red-400 px-2 py-0.5 rounded-lg">
                    Special
                </span>
            ) : null}

            <div
                className="group relative overflow-hidden rounded-lg border border-border/60 bg-zinc-800 cursor-pointer">
                <a href={youtube_url ? `/song?s=${youtube_url}&p=${id}` : `/playlist?p=${id}`}>
                    {youtube_url || image_url ? (
                        <div ref={imgRef}
                             className={`w-full h-50 md:h-75 transition-transform duration-500 group-hover:scale-110 bg-center ${image_url ? 'bg-cover' : 'bg-auto'}`}
                             data-src={(image_url) ? image_url : `https://i1.ytimg.com/vi/${youtube_url}/sddefault.jpg`}
                             role="img"
                             aria-label={`Playlist thumbnail for ${category_name}`}
                             key={youtube_url}
                        ></div>
                    ) : (
                        <div className="w-full h-50 md:h-75 flex items-center justify-center text-gray-500">
                            Empty playlist
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" aria-hidden="true"></div>
                    <div
                        className="absolute inset-0 flex items-center justify-center duration-300 group-hover:opacity-100 opacity-0" aria-hidden="true">
                        <PlayIcon className="w-12 h-12 text-white"/>
                    </div>
                    <h3 className={'font-bold text-left text-white absolute w-full bottom-0 mx-6'}>{category_name}</h3>
                </a>
            </div>
        </div>
    );
}
